// ============================================================
// constants/battery_lut.js
// EV Powertrain Simulator — 배터리 OCV 룩업 테이블 (SST)
//
// ★ SSOT — OCV LUT 기본값의 유일한 출처입니다.
//   - core/physics/battery.js → 이 파일에서 import
//     (globalThis 런타임 오버라이드 기능은 battery.js에서 유지)
//   - 셀 화학계별 OCV 특성 변경 시 이 파일만 수정.
//
// 데이터 기준: NMC 계열 근사 (25°C 기준)
// ============================================================

// ============================================================
// NMC 622 / NMC 811 공용 OCV LUT — 25°C 기준
// 특징: SOC 0.2~0.3 구간 완만한 기울기(평탄 구간), 만충 4.20V, 방전 종지 3.00V
// 출처: NMC 계열 실측 근사값 (Samsung SDI / CATL 공개 데이터 형상)
// 적용: nmc_622, nmc_811 모두 동일 커브 사용 (화학계 차이는 열화 모델로 분리)
// ============================================================

/**
 * OCV_LUT_SOC_DEFAULT — NMC 계열 OCV-SOC 룩업 테이블 SOC 축
 * 출처: NMC 셀 실측 근사 (25°C 기준)
 * 단위: 무차원 (0~1 정규화 SOC)
 * 사용처: battery.js lookupOCV() 선형 보간 입력
 */
export const OCV_LUT_SOC_DEFAULT = [
  0.00, 0.05, 0.10, 0.15, 0.20, 0.30, 0.40, 0.50, 0.60, 0.70, 0.80, 0.90, 0.95, 1.00,
];

/**
 * OCV_LUT_V_DEFAULT — NMC 계열 OCV-SOC 룩업 테이블 전압 축
 * 출처: NMC 셀 실측 근사 (25°C 기준)
 * 단위: V (셀 전압)
 * 주의: SOC 0.20(3.60V)~0.70(3.92V) 구간은 기울기 완만 → SOC 추정 불확실 구간
 */
export const OCV_LUT_V_DEFAULT = [
  3.00, 3.28, 3.42, 3.52, 3.60, 3.68, 3.74, 3.79, 3.85, 3.92, 3.99, 4.08, 4.14, 4.20,
];

/**
 * OCV_TEMP_SOC_DEFAULT — 온도 보정용 SOC 축
 * 출처: NMC 셀 온도별 OCV 실측 (공개 문헌 근사)
 * 단위: 무차원 (0~1)
 * 사용처: battery.js OCV 온도 보정 선형 보간
 */
export const OCV_TEMP_SOC_DEFAULT = [
  0.00, 0.10, 0.20, 0.30, 0.40, 0.50, 0.60, 0.70, 0.80, 0.90, 1.00,
];

/**
 * OCV_TEMP_DV_N20_DEFAULT — NMC 온도 보정 ΔV (−20°C 기준)
 * 단위: V (25°C OCV 대비 편차)
 * 근거: 저온에서 전해질 이온 전도도 감소 → OCV 하강. SOC 낮을수록 편차 큼.
 */
export const OCV_TEMP_DV_N20_DEFAULT = [
  -0.22, -0.18, -0.14, -0.11, -0.09, -0.07, -0.06, -0.05, -0.04, -0.03, -0.02,
];

/**
 * OCV_TEMP_DV_P0_DEFAULT — NMC 온도 보정 ΔV (0°C 기준)
 * 단위: V (25°C OCV 대비 편차)
 */
export const OCV_TEMP_DV_P0_DEFAULT = [
  -0.10, -0.08, -0.06, -0.05, -0.04, -0.03, -0.025, -0.02, -0.015, -0.01, -0.005,
];

/**
 * OCV_TEMP_DV_P45_DEFAULT — NMC 온도 보정 ΔV (+45°C 기준)
 * 단위: V (25°C OCV 대비 편차)
 * 근거: 고온에서 전극 반응 속도 향상 → 소폭 OCV 상승 (열역학적 활성도 증가)
 */
export const OCV_TEMP_DV_P45_DEFAULT = [
  0.02, 0.018, 0.015, 0.013, 0.010, 0.008, 0.006, 0.004, 0.003, 0.002, 0.001,
];

// ============================================================
// LFP (LiFePO₄) OCV LUT — 25°C 기준
// 특징: 3.2V 플래토 구간(SOC 0.2~0.8), 만충 3.65V, 방전 종지 2.5V
// 출처: 실측 근사값 (A123 26650 기준 형상)
// ============================================================

/** LFP OCV-SOC 테이블 SOC 축 */
export const OCV_LUT_SOC_LFP = [
  0.00, 0.05, 0.10, 0.20, 0.30, 0.40, 0.50, 0.60, 0.70, 0.80, 0.90, 0.95, 1.00,
];

/** LFP OCV-SOC 테이블 전압 축 [V] */
export const OCV_LUT_V_LFP = [
  2.50, 3.00, 3.10, 3.20, 3.22, 3.23, 3.24, 3.25, 3.26, 3.28, 3.35, 3.55, 3.65,
];

/** LFP 온도 보정 ΔV — −20°C 기준 [V] (플래토 구간 민감도 낮음) */
export const OCV_TEMP_DV_N20_LFP = [
  -0.18, -0.14, -0.10, -0.07, -0.05, -0.04, -0.03, -0.03, -0.02, -0.02, -0.01, -0.01, -0.01,
];

/** LFP 온도 보정 ΔV — 0°C 기준 [V] */
export const OCV_TEMP_DV_P0_LFP = [
  -0.08, -0.06, -0.04, -0.03, -0.02, -0.015, -0.01, -0.01, -0.008, -0.006, -0.004, -0.003, -0.002,
];

/** LFP 온도 보정 ΔV — +45°C 기준 [V] */
export const OCV_TEMP_DV_P45_LFP = [
  0.01, 0.009, 0.008, 0.007, 0.006, 0.005, 0.004, 0.003, 0.002, 0.002, 0.001, 0.001, 0.001,
];

// ============================================================
// NCA (LiNiCoAlO₂) OCV LUT — 25°C 기준
// 특징: NMC보다 높은 만충 전압(4.25V), 방전 종지 2.75V,
//       SOC 0.5~0.9 구간 기울기 NMC보다 완만
// 출처: 파나소닉 NCR18650 계열 근사값
// ============================================================

/** NCA OCV-SOC 테이블 SOC 축 */
export const OCV_LUT_SOC_NCA = [
  0.00, 0.05, 0.10, 0.15, 0.20, 0.30, 0.40, 0.50, 0.60, 0.70, 0.80, 0.90, 0.95, 1.00,
];

/** NCA OCV-SOC 테이블 전압 축 [V] */
export const OCV_LUT_V_NCA = [
  2.75, 3.20, 3.48, 3.60, 3.68, 3.76, 3.82, 3.88, 3.94, 4.00, 4.07, 4.14, 4.19, 4.25,
];

/** NCA 온도 보정 ΔV — −20°C 기준 [V] */
export const OCV_TEMP_DV_N20_NCA = [
  -0.25, -0.20, -0.16, -0.13, -0.10, -0.08, -0.065, -0.055, -0.045, -0.035, -0.025, -0.018, -0.012, -0.008,
];

/** NCA 온도 보정 ΔV — 0°C 기준 [V] */
export const OCV_TEMP_DV_P0_NCA = [
  -0.12, -0.10, -0.08, -0.065, -0.05, -0.04, -0.032, -0.025, -0.02, -0.015, -0.01, -0.007, -0.005, -0.003,
];

/** NCA 온도 보정 ΔV — +45°C 기준 [V] */
export const OCV_TEMP_DV_P45_NCA = [
  0.022, 0.019, 0.016, 0.014, 0.011, 0.009, 0.007, 0.005, 0.004, 0.003, 0.002, 0.001, 0.001, 0.001,
];

// ============================================================
// 화학계별 OCV LUT 맵 — battery.js에서 soh_profile 키로 분기
// 사용 예: OCV_LUT_BY_CHEMISTRY[params.soh_profile] ?? OCV_LUT_BY_CHEMISTRY.nmc_622
//
// 각 항목 필드:
//   soc    — SOC 축 배열 (무차원 0~1)
//   v      — OCV 전압 축 배열 [V] (25°C 기준)
//   dv_n20 — -20°C 온도 보정 ΔV 배열 [V]
//   dv_p0  —   0°C 온도 보정 ΔV 배열 [V]
//   dv_p45 —  +45°C 온도 보정 ΔV 배열 [V]
// ============================================================
/**
 * OCV_LUT_BY_CHEMISTRY — 화학계별 OCV 룩업 테이블 통합 맵
 * 출처: 각 화학계별 개별 LUT 상수 (위 정의 참조)
 * 사용처: battery.js lookupOCV() — params.soh_profile로 키 분기
 */
export const OCV_LUT_BY_CHEMISTRY = {
  nmc_622: { soc: OCV_LUT_SOC_DEFAULT, v: OCV_LUT_V_DEFAULT,
    dv_n20: OCV_TEMP_DV_N20_DEFAULT, dv_p0: OCV_TEMP_DV_P0_DEFAULT, dv_p45: OCV_TEMP_DV_P45_DEFAULT },
  nmc_811: { soc: OCV_LUT_SOC_DEFAULT, v: OCV_LUT_V_DEFAULT,
    dv_n20: OCV_TEMP_DV_N20_DEFAULT, dv_p0: OCV_TEMP_DV_P0_DEFAULT, dv_p45: OCV_TEMP_DV_P45_DEFAULT },
  lfp:     { soc: OCV_LUT_SOC_LFP,     v: OCV_LUT_V_LFP,
    dv_n20: OCV_TEMP_DV_N20_LFP,     dv_p0: OCV_TEMP_DV_P0_LFP,     dv_p45: OCV_TEMP_DV_P45_LFP },
  nca:     { soc: OCV_LUT_SOC_NCA,     v: OCV_LUT_V_NCA,
    dv_n20: OCV_TEMP_DV_N20_NCA,     dv_p0: OCV_TEMP_DV_P0_NCA,     dv_p45: OCV_TEMP_DV_P45_NCA },
};